import { useEffect, useState } from "react";
import AuiIcon from "../../components/AuiDropdown/AuiIcon";
import AuiButton from "../../components/Buttons/Buttons";
import DropDown from "../../components/DropDown/DropDown";
import Headings from "../../components/Headings/Headings";
import MoreOption from "../../components/MoreOptions/MoreOption";
import SearchInput from "../../components/SearchInput/SearchInput";
import "./UserConsentListing.scss";
import {
  fetchUserConsentListExport,
  fetchUserConsentRequests,
} from "../../services/training-session.service";
import noRecord from "./../../assets/images/no-recs.png";
import AuiPrimaryPagination from "../../components/AuiDropdown/AuiPagination";
import {
  SessionStatusConstants,
  SessionStatusTypes,
} from "../../utils/constants/session-status-constants";
import TableStatus from "../../components/TableStatus/TableStatus";
import { DropdownOption } from "../Organization/OrganizationCreation";
import { useNavigate } from "react-router-dom";
import { CryptoMethods } from "../../utils/crypto-methods";

const UserConsentListing = () => {
  const navigate = useNavigate();
  const [consentList, setConsentList] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [localSearchTerm, setLocalSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [
    selectedRequestStatus,
    setSelectedRequestStatus,
  ] = useState<DropdownOption | null>(null);
  const [requestStatus, setRequestStatus] = useState<DropdownOption | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageOrder = "id dsc";
  const [pagination, setPagination] = useState({
    isFirst: true,
    isLast: false,
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    rowsperpage: 10,
  });

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
  };

  const statusList: DropdownOption[] = [
    { id: 1, name: "Approved" },
    { id: 2, name: "Pending" },
    { id: 3, name: "Rejected" },
  ];

  const fetchTableData = async () => {
    setIsLoading(true);
    setConsentList([]);
    try {
      let encSearchTerm = '';
      if (searchTerm) {
        encSearchTerm = await CryptoMethods.ecbEncryptData(searchTerm);
      }
      const result = await fetchUserConsentRequests({
        search: encSearchTerm,
        status: selectedRequestStatus?.name,
        pageNumber: currentPage,
        pageSize,
        pageOrder
      });
      const consentData = result?.data?.userConsent || [];
      setConsentList(() => [...consentData])
      setPagination({
        isFirst: !!result?._pagination?.isFirst,
        isLast: result?._pagination?.isLast || false,
        totalItems: result?._pagination?.totalCount || 0,
        totalPages: result?._pagination?.totalPages || 0,
        pageNumber: result?._pagination?.pageNumber || 1,
        pageSize: result?._pagination?.pageSize || 10,
        rowsperpage: result?._pagination?.pageSize || 10,
      });
    } catch (error) {
      setConsentList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [currentPage, pageSize, pageOrder, searchTerm, requestStatus]);

  const handleGoClick = () => {
    setSearchTerm(localSearchTerm);
    setRequestStatus(selectedRequestStatus);
    setCurrentPage(1);
  };

  const handleViewClick = (id: number) => {
    navigate(`/user-consent-view/${id}`);
  };

  const exportConsentExcel = () => {
    exportConsentList();
  };

  const exportConsentList = async () => {
    try {
      let encSearchTerm = "";
      if (searchTerm) {
        encSearchTerm = await CryptoMethods.ecbEncryptData(searchTerm);
      }
      const result = await fetchUserConsentListExport({
        search: encSearchTerm,
        status: selectedRequestStatus?.name,
      });

      const consentData = result?.data?.Data?.url;

      if (result?.status === 200 && consentData) {
        window.open(consentData, "_self");
      }
    } catch (error) {
      console.error("Error while fetching consent request", error);
    }
  };

  return (
    <div className="container my-4 userConsentlistingContainer">
      <div className="ml-1">
        <aui-breadcrumb
          previouspages='[{"title":"All Courses","url":"/course-listing"}]'
          currentpage="Consent Requests"
        ></aui-breadcrumb>
      </div>
      <div className="border-bottom">
        <Headings size="h1" className="pr-3 mb-2">
          Consent Requests
        </Headings>
      </div>
      <div className="row d-flex justify-content-end align-items-end my-3">
        <div className="col-12 col-md-5 col-lg-7">
          <SearchInput
            localSearchTerm={localSearchTerm}
            setLocalSearchTerm={setLocalSearchTerm}
            onClear={handleClearSearch}
            placeholder="Search by Name or Email"
          ></SearchInput>
        </div>
        <div className="col-12 col-md-5 col-lg-4">
          <DropDown
            dropdownLabel="Status"
            dropId="statustypes"
            placeHolder="All status"
            data={statusList.map((status) => status.name)}
            listInBtn
            onChange={(value) => {
              const selectedOption = statusList.find(
                (option) => option.name === value
              );
              setSelectedRequestStatus(selectedOption || null);
            }}
          />
        </div>
        <div className="col-12 col-md-2 col-lg-1 p-0 mt-3 mt-md-0 pl-3 pl-md-0">
          <AuiButton
            variant="primary"
            buttontitle="Go"
            modalid="requestFilter"
            type="button"
            size="medium"
            buttonid={""}
            onClick={handleGoClick}
          ></AuiButton>
        </div>
      </div>
      <div className="userConsentTable my-4">
        <div className="col-12 d-flex justify-content-between px-0">
          <output className="item-count mb-3" aria-live="polite">
            {consentList.length
              ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(
                  currentPage * pageSize,
                  pagination.totalItems
                )} items out of ${pagination.totalItems}`
              : ""}
          </output>
          {consentList.length > 0 && (
            <div className="d-flex align-items-center export">
              <aui-icon
                block={true}
                svgclass="aui-icon-export"
                pathclass="aui-path-export"
                icon="export"
                svgwidth="20"
                svgheight="20"
              ></aui-icon>
              <button
                className="export-text export-btn ml-1"
                onClick={exportConsentExcel}
              >
                Export
              </button>
              <button className="tooltip">
                <aui-icon
                  svgclass="aui-icon-info"
                  pathclass="aui-path-info"
                  icon="info"
                  svgwidth="18"
                  svgheight="18"
                ></aui-icon>
                <span className="tooltiptext">
                  The export will include all data, along with additional
                  fields.
                </span>
              </button>
            </div>
          )}
        </div>
        {isLoading && (
          <div className="aui-loader loaderHeight">
            <aui-loader type="content"></aui-loader>
          </div>
        )}
        {!isLoading &&
          (consentList.length > 0 && (
            <>
              <h2 className="sr-only" id="content-course-table-title">
                Course Consent Requests List
              </h2>
              <section className="course-table" aria-labelledby="content-course-table-title">
              <aui-table
                type="status"
                headerbackground={true}
                headerborder={true}
              >
                <table>
                  <thead>
                    <tr>
                      <th scope="col">
                        <div className="aui-th">First Name</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th">Last Name</div>
                      </th>
                      <th scope="col">
                        <div className="aui-th">Email</div>
                      </th>
                      <th scope="col" className="statusHeader">
                        <div className="aui-th">Status</div>
                      </th>
                      <th scope="col" className="actionHeader">
                        <div className="aui-th d-flex justify-content-lg-center pl-3">
                          Action
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {consentList.map((user, index) => {
                      const { id, firstName, lastName, email, status } = user;
                      const labelId = `user-${firstName.replace(/\s+/g, '-')}-${lastName.replace(/\s+/g, '-')}-${id || index}`;
                      return (
                        <tr
                          className={`table-tr ${
                            SessionStatusConstants[status as SessionStatusTypes]
                              .auiTableRowBorderClass
                          }`}
                          key={id || index}
                        >
                          <td data-title="First Name">
                            <div className="aui-td" id={`firstName-${labelId}`}>{firstName}</div>
                          </td>
                          <td data-title="Last Name">
                            <div className="aui-td" id={`lastName-${labelId}`}>{lastName}</div>
                          </td>
                          <td data-title="Email">
                            <div className="aui-td">{email}</div>
                          </td>
                          <td data-title="Status">
                            <div className="aui-td">
                              <TableStatus status={status}></TableStatus>
                            </div>
                          </td>
                          <td data-title="Program Type">
                            <div className="d-flex position-relative aui-td justify-content-lg-center">
                              <MoreOption
                                ariaLabel="More options"
                                className="text-center"
                                labelId={labelId}
                              >
                                <ul>
                                  <li>
                                    <AuiIcon iconClass="view" />
                                    <AuiButton
                                      variant="button-text-styled"
                                      buttontitle="View"
                                      modalid="exampleModal"
                                      size="small"
                                      buttonid={""}
                                      buttonclass="actionBtn px-2"
                                      buttonClick={() => handleViewClick(id)}
                                    ></AuiButton>
                                  </li>
                                </ul>
                              </MoreOption>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </aui-table>
            </section>
            </>
          ))}
          {!consentList.length && !isLoading && (
            <div className="aui-status-table-emptystate aui-table-empty">
              <div className="aui-table-no-data d-flex flex-column justify-content-center align-items-center">
                <img src={noRecord} alt="No Data" />
                <h2 className="mb-0">No Data Found</h2>
              </div>
            </div>
          )}
        {consentList.length ? (
          <section className="pagination py-3 secPagination">
            <div className="w-100">
              <AuiPrimaryPagination
                inputdata={pagination}
                totalitems={pagination.totalItems}
                rowsperpage={pagination.rowsperpage}
                setCurrentPage={setCurrentPage}
                setPageSize={handlePageSizeChange}
              />
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};
export default UserConsentListing;
